import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setSelfie, processSelfie, clearFormError, setRegistrationLoading } from '../store/slices/userSlice';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';
import ErrorAlert from '../components/shared/ErrorAlert';
import { compressImage, getDataUrlSizeKB } from '../utils/functions';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

// Helper function to get visitor ID with fallback (Frontend-only version)
const getVisitorIdWithFallback = (userState: any): string => {
    // Try Redux first
    if (userState.visitorId) return userState.visitorId;
    
    // Try localStorage as fallback
    const localStorageVisitorId = localStorage.getItem('visitorId');
    if (localStorageVisitorId) return localStorageVisitorId;
    
    // Generate new anonymous ID as last resort
    const anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitorId', anonymousId);
    return anonymousId;
};

export default function SelfieCapture() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userState = useAppSelector(state => state.user);
    const { selfieUrl, visitorId, isLoading, formError, name, email, ageGroup, nationality } = userState;
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // Debug Redux state
    console.log('🔍 SelfieCapture Redux state:', {
        visitorId,
        name,
        email,
        ageGroup,
        nationality,
        isLoading,
        formError,
        hasSelfie: !!selfieUrl
    });
    
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [photo, setPhoto] = useState<string | null>(selfieUrl || null);
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraError, setCameraError] = useState(false);
    const [processInProgress, setProcessInProgress] = useState(false);

    // Use the custom hook for scroll detection
    const isAtBottom = useScrollToBottom({
        containerSelector: '.main-content-wrapper'
    });
    
    // Camera manager for better cleanup
    const cameraManagerRef = useRef<{
        stream: MediaStream | null;
        destroy: () => void;
    } | null>(null);

    // Function to stop camera stream (moved outside useEffect for better reliability)
    const stopCamera = () => {
        if (cameraManagerRef.current) {
            cameraManagerRef.current.destroy();
            cameraManagerRef.current = null;
        }
        
        // Additional cleanup: Ensure video element is completely cleared
        const videoElement = videoRef.current;
        if (videoElement) {
            // Pause video first
            videoElement.pause();
            
            // Clear all possible sources
            videoElement.srcObject = null;
            videoElement.src = '';
            
            // Force reload to release any remaining references
            videoElement.load();
            
            // Remove any event listeners that might hold references
            videoElement.onloadedmetadata = null;
            videoElement.onplay = null;
        }
        
        setCameraActive(false);
    };

    // Start the camera when component mounts
    useEffect(() => {
        startCamera();

        return () => {
            // Cleanup camera stream
            stopCamera();
        };
    }, []);

    // Clear form error when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearFormError());
        };
    }, [dispatch]);
    
    const startCamera = async () => {
        try {
            setCameraError(false);
            
            // Check if HTTPS is required but not available
            if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
                console.error('HTTPS required for camera access on mobile devices');
                setCameraError(true);
                return;
            }
            
            // Stop any existing camera first
            if (cameraManagerRef.current) {
                cameraManagerRef.current.destroy();
            }
            
            // Record timestamp when camera is accessed for cleanup tracking
            localStorage.setItem('lastCameraAccess', Date.now().toString());
            
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { 
                    facingMode: 'user',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                } 
            });
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setCameraActive(true);
                
                // Create camera manager with destroy method
                cameraManagerRef.current = {
                    stream,
                    destroy: () => {
                        // Get all tracks and stop them aggressively
                        const tracks = stream.getTracks();
                        tracks.forEach(track => {
                            track.stop();
                            // Force track to end state
                            if (track.readyState !== 'ended') {
                                console.warn('Track not properly ended, forcing stop');
                            }
                        });
                        
                        // Clear video element immediately
                        if (videoRef.current) {
                            videoRef.current.pause();
                            videoRef.current.srcObject = null;
                            videoRef.current.src = '';
                            videoRef.current.load();
                        }
                        
                        // Nullify stream reference
                        try {
                            // Additional cleanup for stubborn browsers
                            if (stream.active) {
                                console.warn('Stream still active after stopping tracks');
                            }
                        } catch (e) {
                            // Stream might already be disposed
                        }
                    }
                };
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            setCameraError(true);
        }
    };
    
    const captureSelfie = async () => {
        if (!videoRef.current) return;
        
        // Create a canvas element to capture the video frame
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        
        // Draw the video frame to the canvas
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Flip horizontally to mirror the image
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(videoRef.current, 0, 0);
        }
        
        // Stop the camera after capturing the frame
        stopCamera();

        let dataUrl: string = '';
        try {
            // Compress the image to ensure it's under 5MB
            dataUrl = await compressImage(canvas, 5120); // 5120KB = 5MB
            const fileSizeKB = getDataUrlSizeKB(dataUrl);
            console.log(`📸 Selfie captured and compressed to ${Math.round(fileSizeKB)}KB`);
        } catch (error) {
            console.error('❌ Error compressing selfie:', error);
            // Fallback to JPEG format with default quality if compression fails
            dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            console.log('📸 Using fallback JPEG compression');
        }
        
        // Save to component state and Redux
        setPhoto(dataUrl);
        dispatch(setSelfie(dataUrl));
        
        // Log final result
        const finalSizeKB = Math.round(dataUrl.length / 1024);
        console.log(`✅ Selfie captured: ${finalSizeKB}KB`);
        
        if (finalSizeKB > 3000) {
            console.warn(`⚠️ Large selfie: ${finalSizeKB}KB - may cause storage issues`);
        }
    };
    
    const handleRetake = () => {
        setPhoto(null);
        // Clear any form errors
        if (formError) {
            dispatch(clearFormError());
        }
        startCamera();
    };
    
            // Frontend-only selfie processing (replaces API upload)
    const handleContinue = () => {
        // Ensure camera is stopped before continuing
        stopCamera();
        
        // Get effective visitor ID with fallback
        const effectiveVisitorId = getVisitorIdWithFallback(userState);
        
        console.log('🔍 Frontend-only selfie processing:', {
            visitorId: effectiveVisitorId,
            hasPhoto: !!photo,
            photoLength: photo?.length
        });
        
        // If we have a photo, process it (frontend-only)
        if (photo && !processInProgress) {
            setProcessInProgress(true);
            dispatch(setRegistrationLoading(true));
            
            // Simulate processing time for better UX with proper error handling
            setTimeout(() => {
                try {
                    // Check photo size before processing
                    const photoSizeKB = Math.round(photo.length / 1024);
                    console.log(`📊 Processing selfie: ${photoSizeKB}KB`);
                    
                    // Warn if photo is very large (might cause localStorage issues)
                    if (photoSizeKB > 3000) { // 3MB
                        console.warn(`⚠️ Large selfie detected: ${photoSizeKB}KB - may cause storage issues`);
                    }
                    
                    // Process the selfie through Redux
                    dispatch(processSelfie({
                        selfieDataUrl: photo,
                        filename: `selfie_${effectiveVisitorId}_${Date.now()}.jpg`
                    }));
                    
                    console.log('✅ Frontend-only selfie processing completed');
                    
                    // Reset loading states before navigation
                    setProcessInProgress(false);
                    dispatch(setRegistrationLoading(false));
                    
                    // Navigate to next step
                    navigate('/ready-to-explore');
                } catch (error) {
                    console.error('❌ Error during selfie processing:', error);
                    
                    // Reset loading states
                    setProcessInProgress(false);
                    dispatch(setRegistrationLoading(false));
                    
                    // Show user-friendly error
                    dispatch(clearFormError());
                    
                    // Still navigate to continue the flow, but without the selfie
                    console.log('🔄 Continuing without selfie due to error');
                    navigate('/ready-to-explore');
                }
            }, 800); // Short delay for better UX
        } else {
            // No photo, just continue
            console.log('🔄 No photo to process - continuing to explore');
            navigate('/ready-to-explore');
        }
    };

    const handleSkip = () => {
        // Ensure camera is stopped before skipping
        stopCamera();
        navigate('/ready-to-explore');
    };

    const renderContent = () => {
        if (cameraError) {
            return (
                <>
                    <div className="error-message mx-8 my-6 max-w-sm">
                        <p dangerouslySetInnerHTML={{ __html: t('selfieCapture.cameraError') }} />
                    </div>
                    
                    <div className={`button-wrapper stick-to-bottom flex flex-col gap-4 p-4 !bottom-[4vw] !max-w-[92vw] rounded-lg has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-black-with-border'
                    }`}>
                        <TcButton
                            handleClick={startCamera}
                            primary
                            classNameStyle="mt-4"
                        >
                            {t('common.tryAgain')}
                        </TcButton>
                        
                        <button
                            onClick={handleSkip}
                            className="text-white hover:text-gray-200 py-2 text-base"
                            disabled={isLoading || processInProgress}
                        >
                            {t('common.skip')}
                        </button>
                    </div>
                </>
            );
        }

        if (photo) {
            return (
                <div className="pb-0 max-w-lg mx-auto">
                    <TcPaper classNameStyle="mb-4">
                        <h1 className="main-content-title"
                            dangerouslySetInnerHTML={{ __html: t('selfieCapture.lookingGood') }}
                        />
                    </TcPaper>

                    {/* Form Error Display */}
                    {formError && (
                        <div className="px-8 max-w-lg mx-auto mb-4">
                            <ErrorAlert 
                                message={formError}
                                onDismiss={() => dispatch(clearFormError())}
                            />
                        </div>
                    )}

                    <div className="relative max-w-[288px] max-h-[288px] mx-auto mb-4">
                        <img
                            src={photo}
                            alt={t('selfieCapture.capturedSelfie')}
                            className="w-72 h-72 mx-auto object-cover rounded-full border-2 border-blue-500"
                        />
                    </div>

                    <TcPaper classNameStyle="mt-6">
                        <p
                            dangerouslySetInnerHTML={{ __html: t('selfieCapture.captureSelfieDescription') }}
                        />
                    </TcPaper>

                    <div className={`button-wrapper stick-to-bottom flex flex-col gap-4 p-4 !bottom-[4vw] !max-w-[92vw] rounded-lg has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-black-with-border'
                    }`}>
                        <TcButton 
                            handleClick={handleRetake}
                            disabled={isLoading || processInProgress}
                        >
                            {t('selfieCapture.retake')}
                        </TcButton>

                        <TcButton 
                            handleClick={handleContinue} 
                            primary
                            disabled={isLoading || processInProgress}
                        >
                            {(isLoading || processInProgress) ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    {processInProgress ? t('common.processing') : t('common.loading')}
                                </div>
                            ) : (
                                t('common.continue')
                            )}
                        </TcButton>
                    </div>
                </div>
            );
        }

        // Default: camera view
        return (
            <div className="pb-0 max-w-lg mx-auto">
                {/* Form Error Display */}
                {formError && (
                    <ErrorAlert 
                        message={formError}
                        onDismiss={() => dispatch(clearFormError())}
                    />
                )}

                <div className="relative max-w-[288px] max-h-[288px] mx-auto mt-4 mb-4 rounded-full overflow-hidden border-2 border-blue-500">
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-72 h-72 mx-auto object-cover mirror-mode"
                        style={{ transform: 'scaleX(-1)' }}
                    />
                </div>

                {cameraActive && (
                    <TcPaper classNameStyle="mt-6">
                        <h1 className="main-content-title">
                            {t('selfieCapture.sayCheese')}
                        </h1>
                        <p className="mt-4"
                            dangerouslySetInnerHTML={{ __html: t('selfieCapture.description') }}
                        />
                    </TcPaper>
                )}

                {cameraActive && (
                    <div className={`button-wrapper stick-to-bottom flex flex-col gap-4 p-4 !bottom-[4vw] !max-w-[92vw] has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-black-with-border'
                    }`}>
                        <TcButton
                            handleClick={captureSelfie}
                            primary
                            disabled={isLoading}
                        >
                            {t('selfieCapture.captureSelfie')}
                        </TcButton>

                        <button
                            onClick={handleSkip}
                            className="text-white hover:text-gray-200 py-2 text-base"
                            disabled={isLoading || processInProgress}
                        >
                            {t('common.skip')}
                        </button>
                    </div>
                )}
            </div>
        );
    };
    
    return (
        <div className={`
            main-content-wrapper 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            {renderContent()}
        </div>
    );
}